import React, { useEffect, useState } from "react";
import { fetchClutchMoments } from "../lib/api";
import { AvatarChip } from "./AvatarChip";
import { MatchVideoEmbed } from "./MatchVideoEmbed";

/**
 * ClutchMomentsRail — horizontally scrollable row of recent 🔥 clutch matches
 * with embedded videos. Visible on PlayerDashboard + Player public profile.
 *
 * Props:
 *   userId?: scope to a single player's team encounters
 *   leagueId?: scope to a single league
 *   limit?: number (default 8)
 *   title?: override the rail title
 *   onOpenLeague?: callback when the league badge is clicked
 */
export function ClutchMomentsRail({ userId, leagueId, limit = 8, title, onOpenLeague }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = { limit };
    if (userId) params.user_id = userId;
    if (leagueId) params.league_id = leagueId;
    fetchClutchMoments(params)
      .then((d) => { if (alive) { setItems(Array.isArray(d) ? d : []); setLoading(false); } })
      .catch(() => { if (alive) { setItems([]); setLoading(false); } });
    return () => { alive = false; };
  }, [userId, leagueId, limit]);

  if (loading) return null;
  if (items.length === 0) return null;

  return (
    <section
      data-testid="clutch-moments-rail"
      className="glass-strong rounded-3xl border-2 border-[#14b8a6]/40 p-5 sm:p-6"
    >
      <div className="flex items-center justify-between mb-3">
        <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">
          <i className="fas fa-fire mr-1" />
          {title || "CLUTCH MOMENTS"}
          <span className="text-white/45 normal-case tracking-normal ml-2 text-[10px]">
            · self-marked highlights with video
          </span>
        </p>
        <span data-testid="clutch-count" className="text-[10px] tracking-widest text-[#14b8a6]/80">
          {items.length} CLIP{items.length === 1 ? "" : "S"}
        </span>
      </div>

      <ul
        className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-2"
        style={{ scrollbarWidth: "thin" }}
      >
        {items.map((m) => (
          <li
            key={m.challenge_id}
            data-testid={`clutch-card-${m.challenge_id}`}
            className="snap-start min-w-[280px] sm:min-w-[360px] rounded-2xl border border-[#14b8a6]/30 bg-black/40 overflow-hidden"
          >
            <MatchVideoEmbed url={m.video_url} />
            <div className="p-3">
              <p className="text-[10px] tracking-widest text-[#14b8a6]/80">
                {(m.league?.game || "").toUpperCase()}
                {m.league?.name && (
                  <button
                    type="button"
                    onClick={() => onOpenLeague?.(m.league_id)}
                    data-testid={`clutch-league-${m.challenge_id}`}
                    className="ml-1 text-[#a3a3ac] hover:underline"
                  >
                    · {m.league.name}
                  </button>
                )}
              </p>
              <div className="flex items-center justify-between mt-1">
                <div className="flex items-center gap-2 min-w-0">
                  <AvatarChip avatarId={m.from_team?.avatar_id} pfpUrl={m.from_team?.pfp_url} size={20} />
                  <span className="text-xs text-white truncate">{m.from_team?.name || "—"}</span>
                </div>
                <span className="font-display text-sm">
                  <span className={m.winner_team_id === m.from_team_id ? "text-[#14b8a6]" : "text-white/45"}>{m.score_a ?? 0}</span>
                  <span className="text-white/30 mx-1">·</span>
                  <span className={m.winner_team_id === m.to_team_id ? "text-[#14b8a6]" : "text-white/45"}>{m.score_b ?? 0}</span>
                </span>
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-xs text-white truncate">{m.to_team?.name || "—"}</span>
                  <AvatarChip avatarId={m.to_team?.avatar_id} pfpUrl={m.to_team?.pfp_url} size={20} />
                </div>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

export default ClutchMomentsRail;
